import React, { FC, useMemo } from 'react';

import {
	EvFilters,
	EvSection,
	IFilterValues,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY
} from '@evinced-private/ui-common';

import { MOBILE_SDK_TEST_FILTERS_SECTION_ID } from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import omit from 'lodash-es/omit';
import { useMobileSdkSingleTestFilters } from 'src/apps/mobile-sdk/hooks/useMobileSdkSingleTestFilters';
import { useMobileSdkTestFilterOptionsQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestFilterOptionsQuery';
import { TMobileSdkTestFilterOptions } from 'src/apps/mobile-sdk/types/MobileSdkServiceTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';

import './MobileSdkSingleTestFiltersSection.scss';

interface IMobileSdkSingleTestFiltersProps {
	testId: string;
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	tableId: string;
	ariaLabel: string;
}

export const MobileSdkSingleTestFiltersSection: FC<IMobileSdkSingleTestFiltersProps> = ({
	testId,
	filters,
	onFilterChange,
	tableId,
	ariaLabel
}) => {
	const filterOptionsQuery = useMobileSdkTestFilterOptionsQuery(testId);

	const { filterDefinitions, mergedFilterOptions } = useMobileSdkSingleTestFilters({
		allLabels: filterOptionsQuery.data?.scans ?? [],
		selectedLabels: ((filters as TMobileSdkTestFilterOptions)?.scans as OptionType[]) ?? [],
		filterOptions: filterOptionsQuery.data ?? {}
	});

	const initialValues = useMemo(
		() => ({
			text: { searchValue: filters?.searchValue },
			select: omit(filters, 'searchValue')
		}),
		[filters]
	);

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
		onFilterChange(tableId, flatFilters);
	};

	return (
		<EvSection
			ariaLabel={ariaLabel}
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId="filters-section"
			dataAutomationId={MOBILE_SDK_TEST_FILTERS_SECTION_ID}
			className="mobile-sdk-single-test-filters-section"
		>
			<EvFilters
				className="data-filters"
				onApply={handleApply}
				onReset={handleApply}
				initialValues={initialValues}
				filterOptions={mergedFilterOptions}
				isFilterOptionsLoaded={!filterOptionsQuery.isLoading}
				filterDefinitions={filterDefinitions}
				shouldSyncValuesWithAvailableOptions={false}
			/>
		</EvSection>
	);
};
